import { useCallback, useState } from 'react';
import type { TaskAssignee, TaskSummary } from '@todo/shared';
import { useAuth } from '../hooks/useAuth';
import { useTaskActions } from '../hooks/useTaskActions';
import { useColumnBoard } from '../hooks/useColumnBoard';
import { useDragToStatus } from '../hooks/useDragToStatus';
import { TaskDetailPanel } from '../components/TaskDetailPanel';
import { BlockReasonModal } from '../components/BlockReasonModal';
import { TaskCard, TaskListError, TaskListEmpty } from '../components/TaskCard';
import { TaskBoardPaged, WORK_COLUMNS } from '../components/TaskBoard';

/**
 * Module 10 - Assigned to me (Delegation).
 *
 * The same data as /tasks?scope=assigned-to-me, laid out as a board like My
 * Day. On this page the reader is the assignee, not the manager; assignment
 * is final, so a task simply appears here the moment someone hands it over.
 *
 * Each status column loads and pages independently (see useColumnBoard).
 */

const FILTERS = { scope: 'assigned-to-me' as const };

export default function AssignedToMePage() {
  const { user } = useAuth();
  const [flash, setFlash] = useState<string | null>(null);
  const [viewing, setViewing] = useState<TaskSummary | null>(null);

  const board = useColumnBoard(WORK_COLUMNS, FILTERS);

  function replace(updated: TaskSummary) {
    board.mutate(updated);
  }

  const actions = useTaskActions(replace, setFlash);
  const drag = useDragToStatus(
    (id) => board.allLoaded.find((t) => t.id === id),
    replace,
    setFlash,
  );

  /** My own assignment row on a task - the thing this page is grouped by. */
  const mine = useCallback(
    (task: TaskSummary): TaskAssignee | undefined =>
      task.assignees.find((a) => a.userId === user?.id),
    [user?.id],
  );



  return (
    <div className="mx-auto  px-8 py-8">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Assigned to me</h1>
        <p className="mt-1 text-sm text-slate-500">
          {board.loading
            ? 'Loading...'
            : `${board.totalCount} ${board.totalCount === 1 ? 'task' : 'tasks'} on your plate`}
        </p>
      </header>

      {flash && (
        <div role="status" className="mt-5 flex items-start justify-between gap-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-800 ring-1 ring-emerald-200">
          <span>{flash}</span>
          <button type="button" onClick={() => setFlash(null)} aria-label="Dismiss"
                  className="text-emerald-600 hover:text-emerald-800">&times;</button>
        </div>
      )}

      <div className="mt-6">
        {board.error ? (
          <TaskListError message={board.error} onRetry={() => void board.reload()} />
        ) : !board.loading && board.totalCount === 0 ? (
          <TaskListEmpty
            title="Nothing assigned to you"
            hint="Tasks someone hands to you will show up here first."
          />
        ) : (
          <TaskBoardPaged
            state={board.state}
            loading={board.loading}
            onLoadMore={(column) => void board.loadMore(column)}
            onDropTask={drag.onDropTask}
            renderCard={(task, subtasks) => {
              const me = mine(task);
              const unassigned = task.assignees.length === 0;
              return (
                <TaskCard
                  key={task.id}
                  task={task}
                  subtasks={subtasks}
                  draggable
                  busy={actions.busyId === task.id || drag.busyId === task.id}
                  onOpen={setViewing}
                  onToggleDone={me ? (t) => void actions.toggleDone(t) : undefined}
                  onToggleImportant={(t) => void actions.toggleImportant(t)}
                  hideAssignee={!unassigned}
                />
              );
            }}
          />
        )}
      </div>

      {drag.blocking && (
        <BlockReasonModal
          task={drag.blocking}
          reason={drag.blockReason}
          busy={drag.busyId === drag.blocking.id}
          onReasonChange={drag.setBlockReason}
          onConfirm={drag.confirmBlock}
          onCancel={drag.cancelBlock}
        />
      )}

      {viewing && (
        <TaskDetailPanel
          task={viewing}
          onClose={() => setViewing(null)}
          onChanged={(t) => {
            replace(t);
            setViewing((v) => (v && v.id === t.id ? t : v));
          }}
        />
      )}
    </div>
  );
}
